import { useMemo } from 'react';
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/style.css';
import './DatePicker.css';

interface DatePickerProps {
  value: string | null;
  onChange: (value: string) => void;
  minDate?: Date;
  label?: string;
}

const parseISODate = (s: string | null | undefined): Date | undefined => {
  if (!s) return undefined;
  const [y, m, d] = s.split('-').map(Number);
  if (!y || !m || !d) return undefined;
  return new Date(y, m - 1, d);
};

const formatISODate = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const startOfToday = (): Date => {
  const t = new Date();
  t.setHours(0, 0, 0, 0);
  return t;
};

export default function DatePicker({ value, onChange, minDate, label }: DatePickerProps) {
  const selected = useMemo(() => parseISODate(value), [value]);
  const min = useMemo(() => minDate ?? startOfToday(), [minDate]);

  const handleSelect = (date: Date | undefined) => {
    onChange(date ? formatISODate(date) : '');
  };

  return (
    <div className="w-full">
      {label && (
        <div className="flex items-center justify-between mb-2">
          <label className="block text-sm font-medium font-sans text-text-secondary">
            {label}
          </label>
          {selected && (
            <button
              type="button"
              onClick={() => onChange('')}
              className="font-sans text-sm text-primary hover:text-primary-dark cursor-pointer"
            >
              Clear
            </button>
          )}
        </div>
      )}
      <div className="rdp-citizenly rounded-xl border-2 border-border bg-card px-2 py-2 sm:px-3">
        <DayPicker
          mode="single"
          selected={selected}
          onSelect={handleSelect}
          disabled={{ before: min }}
          startMonth={min}
          defaultMonth={selected ?? min}
          showOutsideDays
          fixedWeeks
        />
      </div>
    </div>
  );
}
